import { useQuery } from '@tanstack/react-query';
import type { TaskStatus, LaborBucket, PaginationResponse } from 'scheduler-types';
import { apiFetch } from '../lib/api';

// Task as returned by GET /api/v1/tasks (schedule query)
export interface ScheduleTask {
  id: string;
  partNumber: string;
  laborBucket: LaborBucket;
  assignedPerson: { id: string; name: string } | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  status: TaskStatus;
  gpSalesOrderNumber: string;
  // Additional fields for detailed view
  lineItemId?: string;
  estimatedMinutesMin?: number;
  estimatedMinutesMax?: number;
  actualMinutes?: number;
  isPinned?: boolean;
  dependsOnParts?: boolean;
}

interface ScheduleResponse {
  data: ScheduleTask[];
  scheduleVersion: number;
  pagination: PaginationResponse;
}

interface UseScheduleOptions {
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  skillId?: string;
  assignedPersonId?: string;
  status?: TaskStatus;
  limit?: number;
}

async function fetchSchedule(options: UseScheduleOptions): Promise<ScheduleResponse> {
  const params = new URLSearchParams();
  params.append('startDate', options.startDate);
  params.append('endDate', options.endDate);

  if (options.skillId) {
    params.append('skillId', options.skillId);
  }
  if (options.assignedPersonId) {
    params.append('assignedPersonId', options.assignedPersonId);
  }
  if (options.status) {
    params.append('status', options.status);
  }
  if (options.limit) {
    params.append('limit', String(options.limit));
  }

  return apiFetch<ScheduleResponse>(`/api/v1/tasks?${params.toString()}`);
}

export function useSchedule(options: UseScheduleOptions) {
  return useQuery({
    queryKey: ['schedule', options],
    queryFn: () => fetchSchedule(options),
    staleTime: 30000,
    enabled: Boolean(options.startDate && options.endDate),
  });
}

// Labor bucket display names and colors
// Keyed by both snake_case enum values (legacy) and dynamic BOM API names
export const laborBucketConfig: Record<string, { label: string; color: string }> = {
  // Legacy snake_case keys (from seed data / hardcoded enums)
  mechanical_engineering: { label: 'Mechanical Eng', color: 'bg-blue-500' },
  electrical_engineering: { label: 'Electrical Eng', color: 'bg-indigo-500' },
  software_design: { label: 'Software Design', color: 'bg-purple-500' },
  image_formation_design: { label: 'Image Formation', color: 'bg-pink-500' },
  software_development: { label: 'Software Dev', color: 'bg-violet-500' },
  fabrication: { label: 'Fabrication', color: 'bg-orange-500' },
  electrical_assembly: { label: 'Electrical Assy', color: 'bg-amber-500' },
  final_assembly: { label: 'Final Assembly', color: 'bg-yellow-500' },
  testing_qc: { label: 'Testing/QC', color: 'bg-green-500' },
  packing_shipping: { label: 'Packing/Ship', color: 'bg-teal-500' },
  procuring: { label: 'Procuring', color: 'bg-cyan-500' },
  waiting_for_parts: { label: 'Waiting Parts', color: 'bg-gray-400' },
  awaiting_confirmation: { label: 'Confirm', color: 'bg-yellow-400' },
  awaiting_shipment: { label: 'Shipment', color: 'bg-yellow-300' },
  awaiting_receipt: { label: 'Receipt', color: 'bg-yellow-200' },
  stage_parts: { label: 'Stage Parts', color: 'bg-lime-500' },
  supply_chain_release: { label: 'Supply Chain', color: 'bg-emerald-500' },

  // Dynamic BOM API labor category names (human-readable)
  'Mechanical Engineering': { label: 'Mechanical Eng', color: 'bg-blue-500' },
  'Electrical Engineering': { label: 'Electrical Eng', color: 'bg-indigo-500' },
  'Software Engineering/Development': { label: 'Software Dev', color: 'bg-violet-500' },
  'Fabrication': { label: 'Fabrication', color: 'bg-orange-500' },
  'Electrical Assembly': { label: 'Electrical Assy', color: 'bg-amber-500' },
  'Mechanical Assembly/Final Assembly': { label: 'Final Assembly', color: 'bg-yellow-500' },
  'Testing and QC': { label: 'Testing/QC', color: 'bg-green-500' },
  'Crating, Packing, Labeling, Freight': { label: 'Packing/Ship', color: 'bg-teal-500' },
};

// Status colors matching design system
export const statusConfig: Record<TaskStatus, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'bg-gray-400' },
  ready: { label: 'Ready', color: 'bg-blue-500' },
  staged: { label: 'Staged', color: 'bg-purple-500' },
  partial_staged: { label: 'Partial Staged', color: 'bg-orange-500' },
  in_progress: { label: 'In Progress', color: 'bg-green-500' },
  complete: { label: 'Complete', color: 'bg-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-300' },
  abandoned: { label: 'Abandoned', color: 'bg-red-300' },
};
